<script lang="ts" setup>
import { computed, onMounted, ref, watch } from "vue"
import { useRoute, useRouter } from "vue-router"

import { useSearch, type SearchEntry } from "@/composables/use-search"
import { useSearchIndex } from "@/composables/use-search-index"

const route = useRoute()
const router = useRouter()

const emit = defineEmits<{ close: [] }>()

const { entries, ensureIndex } = useSearchIndex()
const { searchQuery, fuseResults, getMostRelevantField } = useSearch(entries)

// ─── Autocomplete dropdown ──────────────────────────────────────────────────
const MAX_SUGGESTIONS = 6
const suggestions = computed(() => {
  // Deduplicate by id::uri composite key.
  // - Same id + same uri = same entry returned twice by Fuse (matching on
  //   both name and title fields) → collapse to one result.
  // - Same id + different uri = same student in different group projects
  //   → keep both (each is a distinct project page).
  const seenKeys = new Set<string>()
  return fuseResults.value
    .filter(result => {
      const key = `${result.item.id}::${result.item.uri}`
      if (seenKeys.has(key)) return false
      seenKeys.add(key)
      return true
    })
    .slice(0, MAX_SUGGESTIONS)
})

const activeKey = ref<string | null>(null)
const showSuggestions = ref(false)

const labelFor = (result: (typeof suggestions.value)[number]) =>
  getMostRelevantField(result).value
const kindFor = (item: SearchEntry) =>
  item.programme ? "Programme" : item.isGroup ? "Group Project" : "Project"

watch(suggestions, () => {
  activeKey.value = null
})

watch(searchQuery, query => {
  showSuggestions.value = !!query
})

// ─── Navigation ─────────────────────────────────────────────────────────────
const closeSuggestions = () => {
  showSuggestions.value = false
  activeKey.value = null
}

const goToSuggestion = (item: SearchEntry) => {
  closeSuggestions()
  emit("close")
  router.push(`/${item.uri}`)
}

const submitFullSearch = () => {
  if (!searchQuery.value) return
  closeSuggestions()
  router.push({
    query: { ...route.query, view: "search", q: searchQuery.value },
  })
}

const onKeydown = (e: KeyboardEvent) => {
  const keys = suggestions.value.map(r => `${r.item.id}::${r.item.uri}`)
  const cur = activeKey.value ? keys.indexOf(activeKey.value) : -1
  switch (e.key) {
    case "ArrowDown":
      if (!suggestions.value.length) return
      e.preventDefault()
      activeKey.value = keys[(cur + 1) % keys.length]
      break
    case "ArrowUp":
      if (!suggestions.value.length) return
      e.preventDefault()
      activeKey.value = keys[cur <= 0 ? keys.length - 1 : cur - 1]
      break
    case "Enter":
      if (activeKey.value) {
        const hit = suggestions.value.find(
          r => `${r.item.id}::${r.item.uri}` === activeKey.value
        )
        if (hit) goToSuggestion(hit.item)
      } else {
        submitFullSearch()
      }
      break
    case "Escape":
      closeSuggestions()
      break
  }
}

const onBlur = () => {
  setTimeout(closeSuggestions, 120)
}

onMounted(async () => {
  await ensureIndex()
  searchQuery.value = route.query.q as string
})
</script>

<template>
  <div class="relative">
    <input
      class="inline-block w-full bg-transparent px-2 placeholder:text-current focus:outline-hidden placeholder:opacity-50 transition-opacity"
      v-model="searchQuery"
      :placeholder="'Search...'"
      type="text"
      role="combobox"
      aria-autocomplete="list"
      :aria-expanded="showSuggestions && suggestions.length > 0"
      autocomplete="new-password"
      @keydown="onKeydown"
      @focus="showSuggestions = !!searchQuery"
      @blur="onBlur"
    />

    <ul
      v-if="showSuggestions && suggestions.length"
      class="absolute left-0 right-0 top-full z-40 mt-1 bg-white border rounded overflow-hidden"
      role="listbox"
    >
      <li
        v-for="result in suggestions"
        :key="`${result.item.id}::${result.item.uri}`"
        role="option"
        :aria-selected="`${result.item.id}::${result.item.uri}` === activeKey"
        class="flex items-baseline justify-between gap-2 px-2 py-1 text-sm cursor-pointer transition-colors"
        :class="
          `${result.item.id}::${result.item.uri}` === activeKey
            ? 'bg-blue text-white'
            : ''
        "
        @mousedown.prevent="goToSuggestion(result.item)"
        @mouseenter="activeKey = `${result.item.id}::${result.item.uri}`"
        @mouseleave="activeKey = null"
      >
        <span class="truncate" v-html="labelFor(result)" />
        <span class="shrink-0 opacity-50 text-xs">{{
          kindFor(result.item)
        }}</span>
      </li>
    </ul>
  </div>
</template>